<script setup>
import {defineModel, defineProps, defineExpose, computed} from 'vue'
import TheSelectAdAccounts from '@shared-components/ui/selects/TheSelectAdAccounts.vue'
import {storeToRefs} from 'pinia'
import {useAuthenticationStore} from '@/stores'
import InputFileUploadOrLink from '@shared-components/ui/inputs/files/InputFileUploadOrLink.vue'
import {openUrl} from '@shared/utils/helpers'
import TheCopyInputButton from '@shared-components/ui/buttons/TheCopyInputButton.vue'

const {userCompanyHash} = storeToRefs(useAuthenticationStore())

const modelValue = defineModel('modelValue', {required: true})
const mainFormData = defineModel('mainFormData', {required: true})

// Props & emits
const props = defineProps({
  loading: {
    type: Boolean,
    default: null,
  },
  isCreating: {
    type: Boolean,
    required: false,
    default: true,
  },
})

// Computed
const showLicenseUpload = computed(() => {
  return props.isCreating || modelValue.value.file_tempo_verification_license || modelValue.value.meta.license_link
})

const showAdditionalUpload = computed(() => {
  return (
    props.isCreating || modelValue.value.file_tempo_verification_additional || modelValue.value.meta.additional_link
  )
})

// Methods
const formatFilesToPayload = () => {
  return Object.keys(modelValue.value)
    .filter(key => key.startsWith('file_tempo_'))
    .map(key => modelValue.value[key]?.hash)
    .filter(Boolean)
}

defineExpose({formatFilesToPayload})
</script>

<template>
  <div class="form-group">
    <label for="other-request-ad-account">Ad Account *</label>
    <template v-if="isCreating">
      <the-select-ad-accounts
        v-model="modelValue.ad_account_ids"
        :list-by-search="!!userCompanyHash"
        :payload="{
          platform: 'google',
          company_hash: mainFormData.company_hash,
        }"
        :disabled="!mainFormData.company_hash || loading"
        id="other-request-ad-account"
        required
      />
    </template>
    <template v-else>
      <input
        v-model="modelValue.ad_account_title"
        id="other-request-modal-ad_account_titles"
        class="form-control"
        :disabled="!isCreating"
      />
    </template>
  </div>
  <div class="form-group">
    <label for="other-request-geo">Target Geo *</label>
    <input
      v-model="modelValue.meta.geo"
      type="text"
      id="other-request-geo"
      class="form-control"
      :disabled="loading || !isCreating"
      required
    />
  </div>
  <input-file-upload-or-link
    label="Business Registration Document"
    v-model:model-file="modelValue.file_tempo_business_registration"
    v-model:model-link="modelValue.meta.business_registration_link"
    :document-type="modelValue.meta.business_registration_link ? 'link' : 'file'"
    hint="Examples: Certificate of Incorporation, LLC Registration, EIN Letter, or equivalent official proof of company existence."
    usage-type="advertiser-verification-business-registration"
    id="business-registration"
    :hide-tools="isCreating"
    :disabled="!isCreating"
    required
    class="mb-3"
  />
  <input-file-upload-or-link
    v-if="showLicenseUpload"
    label="License or Certification (If Applicable)"
    v-model:model-file="modelValue.file_tempo_verification_license"
    v-model:model-link="modelValue.meta.license_link"
    :document-type="modelValue.meta.license_link ? 'link' : 'file'"
    usage-type="advertiser-verification-license"
    hint="Required for regulated industries like insurance, finance, gambling, healthcare, legal, crypto, alcohol, etc."
    id="verification-license"
    :hide-tools="isCreating"
    :disabled="!isCreating"
    class="mb-3"
  />
  <input-file-upload-or-link
    label="Letter of Authorization (LOA) or Agency Agreement"
    v-model:model-file="modelValue.file_tempo_verification_loa"
    v-model:model-link="modelValue.meta.loa_link"
    :document-type="modelValue.meta.loa_link ? 'link' : 'file'"
    usage-type="advertiser-verification-loa"
    hint="Must show the agency is allowed to act on behalf of the advertiser. In our case, it should clearly show the relationship between the advertiser and Point2Web."
    id="verification-loa"
    :hide-tools="isCreating"
    :disabled="!isCreating"
    required
    class="mb-3"
  />

  <div class="form-group">
    <label for="other-request-website_url">URL to website *</label>
    <div class="input-group">
      <input
        v-model="modelValue.meta.website_url"
        type="url"
        class="form-control"
        :disabled="loading || !isCreating"
        id="other-request-website_url"
        required
      />
      <span v-if="modelValue.meta.website_url" class="input-group-text gap-2">
        <the-copy-input-button :value="modelValue.meta.website_url" :id="`other-request-website_url-link-copy`" />

        <span
          class="open-in-new-tab-btn clickable-icon"
          @click.prevent="openUrl(modelValue.meta.website_url)"
          v-tooltip="'Open in a new tab'"
        >
          <i class="fa-solid fa-link" />
        </span>
      </span>
    </div>
    <small class="text-muted"
      >Should include: product/service description, contact details, Privacy Policy, Terms of Service, and About Us. A
      simple landing page is not enough.</small
    >
  </div>
  <input-file-upload-or-link
    v-if="showAdditionalUpload"
    label="Any Additional Document"
    v-model:model-file="modelValue.file_tempo_verification_additional"
    v-model:model-link="modelValue.meta.additional_link"
    :document-type="modelValue.meta.additional_link ? 'link' : 'file'"
    usage-type="advertiser-verification-additional"
    id="verification-additional"
    :hide-tools="isCreating"
    :disabled="!isCreating"
    class="mb-3"
  />
</template>
